import React, { useState, useRef, useEffect } from 'react';
import { Video, X, Camera, AlertCircle } from 'lucide-react';
import type { Grade } from '../types';

interface DashcamModeProps {
  isOpen: boolean;
  onClose: () => void;
  onPhotoCapture: (blob: Blob) => Promise<{ success: boolean; grade?: Grade }>;
  audioEnabled: boolean;
}

interface SessionStats {
  total: number;
  gradeA: number;
  gradeB: number;
  gradeC: number;
  gradeD: number;
  skipped: number;
}

interface GradeMarker {
  id: number;
  grade: Grade;
  position: number;
}

export function DashcamMode({ isOpen, onClose, onPhotoCapture, audioEnabled }: DashcamModeProps) {
  const [isRunning, setIsRunning] = useState(false);
  const [sessionStats, setSessionStats] = useState<SessionStats>({
    total: 0,
    gradeA: 0,
    gradeB: 0,
    gradeC: 0,
    gradeD: 0,
    skipped: 0
  });
  const [nextPhotoIn, setNextPhotoIn] = useState<number>(60);
  const [error, setError] = useState<string>('');
  const [stream, setStream] = useState<MediaStream | null>(null);
  const [captureInterval, setCaptureInterval] = useState<30 | 60>(60);
  const [recentGrades, setRecentGrades] = useState<GradeMarker[]>([]);
  const [gradeIdCounter, setGradeIdCounter] = useState(0);

  const videoRef = useRef<HTMLVideoElement>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const countdownRef = useRef<NodeJS.Timeout | null>(null);
  const beepTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  useEffect(() => {
    return () => {
      stopCamera();
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (countdownRef.current) clearInterval(countdownRef.current);
      if (beepTimeoutRef.current) clearTimeout(beepTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (isRunning && audioEnabled) {
      if (nextPhotoIn === 3 || nextPhotoIn === 2 || nextPhotoIn === 1) {
        playBeep(800, 0.1);
      }
    }
  }, [nextPhotoIn, isRunning, audioEnabled]);

  const getAudioContext = () => {
    if (!audioContextRef.current) {
      audioContextRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    return audioContextRef.current;
  };

  const playBeep = (frequency: number, duration: number) => {
    if (!audioEnabled) return;

    try {
      const audioContext = getAudioContext();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.value = frequency;
      oscillator.type = 'sine';

      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + duration);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + duration);
    } catch (error) {
      console.error('Error playing beep:', error);
    }
  };

  const playShutterSound = () => {
    if (!audioEnabled) return;

    try {
      const audioContext = getAudioContext();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();

      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);

      oscillator.frequency.setValueAtTime(1200, audioContext.currentTime);
      oscillator.frequency.exponentialRampToValueAtTime(200, audioContext.currentTime + 0.15);
      oscillator.type = 'square';

      gainNode.gain.setValueAtTime(0.2, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.15);

      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.15);

      console.log('Shutter sound played');
    } catch (error) {
      console.error('Error playing shutter sound:', error);
    }
  };

  const stopCamera = () => {
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
      setStream(null);
    }
  };

  const capturePhoto = async (): Promise<Blob | null> => {
    if (!videoRef.current) return null;

    const canvas = document.createElement('canvas');
    canvas.width = videoRef.current.videoWidth;
    canvas.height = videoRef.current.videoHeight;

    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    ctx.drawImage(videoRef.current, 0, 0);

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        resolve(blob);
      }, 'image/jpeg', 0.8);
    });
  };

  const addGradeMarker = (grade: Grade) => {
    const newMarker: GradeMarker = {
      id: gradeIdCounter,
      grade,
      position: 0
    };

    setGradeIdCounter(prev => prev + 1);
    setRecentGrades(prev => {
      const updated = [newMarker, ...prev].slice(0, 5);
      return updated;
    });
  };

  const processAndCapturePhoto = async () => {
    playShutterSound();

    const blob = await capturePhoto();
    if (blob) {
      try {
        const result = await onPhotoCapture(blob);
        if (result.success && result.grade) {
          setSessionStats(prev => ({
            ...prev,
            total: prev.total + 1,
            [`grade${result.grade}`]: prev[`grade${result.grade}` as keyof SessionStats] + 1
          }));
          addGradeMarker(result.grade);
        } else {
          setSessionStats(prev => ({
            ...prev,
            skipped: prev.skipped + 1
          }));
        }
      } catch (error) {
        console.error('Error processing photo:', error);
        setSessionStats(prev => ({
          ...prev,
          skipped: prev.skipped + 1
        }));
      }
    }
  };

  const startDashcam = async () => {
    setError('');

    try {
      const mediaStream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment',
          width: { ideal: 1920 },
          height: { ideal: 1080 }
        },
        audio: false
      });

      setStream(mediaStream);

      if (videoRef.current) {
        videoRef.current.srcObject = mediaStream;
        await new Promise<void>((resolve) => {
          if (videoRef.current) {
            videoRef.current.onloadedmetadata = () => {
              videoRef.current?.play().then(() => resolve()).catch(err => {
                console.error('Error playing video:', err);
                resolve();
              });
            };
          }
        });
      }

      setIsRunning(true);
      setNextPhotoIn(captureInterval);

      setTimeout(async () => {
        await processAndCapturePhoto();
      }, 1000);

      intervalRef.current = setInterval(async () => {
        await processAndCapturePhoto();
        setNextPhotoIn(captureInterval);
      }, captureInterval * 1000);

      countdownRef.current = setInterval(() => {
        setNextPhotoIn(prev => Math.max(0, prev - 1));
      }, 1000);

    } catch (err) {
      console.error('Error accessing camera:', err);
      setError('Unable to access camera. Please grant camera permissions.');
    }
  };

  const stopDashcam = () => {
    setIsRunning(false);
    stopCamera();

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (countdownRef.current) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
    }
    setNextPhotoIn(captureInterval);
  };

  const handleClose = () => {
    stopDashcam();
    setSessionStats({
      total: 0,
      gradeA: 0,
      gradeB: 0,
      gradeC: 0,
      gradeD: 0,
      skipped: 0
    });
    setRecentGrades([]);
    setError('');
    onClose();
  };

  useEffect(() => {
    if (!isRunning || recentGrades.length === 0) return;

    const animationInterval = setInterval(() => {
      setRecentGrades(prev =>
        prev.map(marker => ({
          ...marker,
          position: marker.position + 2
        })).filter(marker => marker.position < 100)
      );
    }, 50);

    return () => clearInterval(animationInterval);
  }, [isRunning, recentGrades.length]);

  if (!isOpen) return null;

  const getGradeColor = (grade: Grade) => {
    switch (grade) {
      case 'A': return 'bg-green-500';
      case 'B': return 'bg-yellow-500';
      case 'C': return 'bg-orange-500';
      case 'D': return 'bg-red-500';
      default: return 'bg-gray-500';
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-4 border-b sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <Video className="w-6 h-6 text-blue-600" />
            <h2 className="text-xl font-bold text-gray-800">Dashcam Mode</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-gray-600 text-sm">
            Automatically captures and grades photos. Mount your phone to capture the road ahead.
          </p>

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
              <span className="text-sm text-red-700">{error}</span>
            </div>
          )}

          {!isRunning && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Photo Interval
              </label>
              <div className="flex gap-3">
                <button
                  onClick={() => setCaptureInterval(30)}
                  className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
                    captureInterval === 30
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-300'
                  }`}
                >
                  Every 30 seconds
                </button>
                <button
                  onClick={() => setCaptureInterval(60)}
                  className={`flex-1 py-2 px-4 rounded-lg font-semibold transition-all ${
                    captureInterval === 60
                      ? 'bg-blue-500 text-white'
                      : 'bg-white text-gray-700 border border-gray-300 hover:border-blue-300'
                  }`}
                >
                  Every 60 seconds
                </button>
              </div>
            </div>
          )}

          <div className="bg-black rounded-lg overflow-hidden">
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="w-full h-48 object-cover"
              style={{ display: isRunning ? 'block' : 'none' }}
            />
            {!isRunning && (
              <div className="w-full h-48 flex items-center justify-center text-gray-500">
                <Camera className="w-12 h-12" />
              </div>
            )}
          </div>

          {isRunning && (
            <div className="bg-gray-800 rounded-lg p-4 overflow-hidden">
              <h3 className="text-white font-semibold mb-3 text-sm">Recent Road Conditions</h3>
              <div className="relative h-24 bg-gray-700 rounded-lg overflow-hidden">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full h-2 bg-gray-600 relative">
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="w-full h-full bg-gradient-to-r from-gray-600 via-gray-500 to-gray-600"></div>
                    </div>
                    <div className="absolute left-0 right-0 top-0 bottom-0">
                      <div className="absolute left-1/4 top-0 bottom-0 w-px bg-white opacity-30"></div>
                      <div className="absolute left-2/4 top-0 bottom-0 w-px bg-white opacity-30"></div>
                      <div className="absolute left-3/4 top-0 bottom-0 w-px bg-white opacity-30"></div>
                    </div>
                  </div>
                </div>

                {recentGrades.length === 0 && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <p className="text-gray-400 text-sm">Waiting for first photo...</p>
                  </div>
                )}

                {recentGrades.map((marker) => (
                  <div
                    key={marker.id}
                    className="absolute transition-all duration-100 ease-linear"
                    style={{
                      left: `${marker.position}%`,
                      top: '50%',
                      transform: 'translate(-50%, -50%)'
                    }}
                  >
                    <div className={`${getGradeColor(marker.grade)} text-white font-bold text-xs w-8 h-8 rounded-full flex items-center justify-center border-2 border-white shadow-lg`}>
                      {marker.grade}
                    </div>
                  </div>
                ))}

                <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                  <div className="text-white font-bold text-2xl">→</div>
                </div>
              </div>
              <p className="text-gray-400 text-xs mt-2 text-center">
                {recentGrades.length > 0 ? 'Grades move along as new photos are captured' : 'Road visualization will show grades here'}
              </p>
            </div>
          )}

          <div className="bg-gray-50 rounded-lg p-4 space-y-3">
            <h3 className="font-semibold text-gray-700">Session Stats</h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div className="bg-white p-3 rounded">
                <div className="text-gray-500">Total Photos</div>
                <div className="text-2xl font-bold text-gray-800">{sessionStats.total}</div>
              </div>
              <div className="bg-white p-3 rounded">
                <div className="text-gray-500">Next Photo In</div>
                <div className={`text-2xl font-bold ${nextPhotoIn <= 3 && nextPhotoIn > 0 ? 'text-red-600 animate-pulse' : 'text-gray-800'}`}>
                  {isRunning ? `${nextPhotoIn}s` : '-'}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-5 gap-2 text-center text-xs">
              <div className="bg-green-100 text-green-800 p-2 rounded">
                <div className="font-bold text-lg">{sessionStats.gradeA}</div>
                <div>Grade A</div>
              </div>
              <div className="bg-yellow-100 text-yellow-800 p-2 rounded">
                <div className="font-bold text-lg">{sessionStats.gradeB}</div>
                <div>Grade B</div>
              </div>
              <div className="bg-orange-100 text-orange-800 p-2 rounded">
                <div className="font-bold text-lg">{sessionStats.gradeC}</div>
                <div>Grade C</div>
              </div>
              <div className="bg-red-100 text-red-800 p-2 rounded">
                <div className="font-bold text-lg">{sessionStats.gradeD}</div>
                <div>Grade D</div>
              </div>
              <div className="bg-gray-100 text-gray-800 p-2 rounded">
                <div className="font-bold text-lg">{sessionStats.skipped}</div>
                <div>Skipped</div>
              </div>
            </div>
          </div>

          {isRunning ? (
            <button
              onClick={stopDashcam}
              className="w-full bg-red-500 hover:bg-red-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <div className="w-3 h-3 bg-white rounded-full animate-pulse" />
              STOP Recording
            </button>
          ) : (
            <button
              onClick={startDashcam}
              className="w-full bg-green-500 hover:bg-green-600 text-white font-semibold py-3 px-4 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <Camera className="w-5 h-5" />
              START Recording
            </button>
          )}

          <div className="text-xs text-gray-500 text-center">
            Audio feedback: {audioEnabled ? 'Enabled' : 'Disabled'}
            {audioEnabled && ' (Beeps at 3-2-1, shutter sound on capture)'}
          </div>
        </div>
      </div>
    </div>
  );
}
